import { useParams } from 'react-router-dom';
import { t } from 'i18next';

import { useColorMode } from '@chakra-ui/react';
import { Xmark } from '@peacecover/icon';
import { RoundBadge, ThumbnailPhotoCard } from '@peacecover/component';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import useReviewQuery from '../../components/api/queries/useReviewQuery';
import { capitalizeFirstLetter } from '../../utils/TextUtil';
import ProfileAvatar from '../../presentation/my-page/components/ProfileAvatar';
import { ArrowsDownUp } from '../../components/icons/ArrowDownUp';
import { Trans } from 'react-i18next';

const TradingPhotoCardReviewDetailPage = () => {
  const { colorMode } = useColorMode();
  const params = useParams();

  const handleHistoryBack = useHandleHistoryBack();
  const review = useReviewQuery(params.review_id);

  const baseBoxColor =
    'w-[327px] rounded-[24px] px-[20px] pt-[26px] pb-[20px] flex flex-col items-start border';

  const boxColor =
    colorMode === 'dark'
      ? 'dark:border-none bg-[rgba(255,255,255,0.07)] shadow-[0_4px_10px_0_rgba(13,14,18,0.32)]'
      : 'border border-[rgba(255,255,255,0.6)] bg-[linear-gradient(134deg,rgba(255,255,255,0.60)_5.69%,rgba(255,255,255,0.00)_95.22%)] shadow-[0_4px_10px_0_rgba(0,0,0,0.04),_inset_0_0_24px_0_rgba(255,255,255,0.3)] backdrop-blur-[20px]';

  if (!review) return null;

  return (
    <div
      className="min-h-screen pt-[calc(env(safe-area-inset-top))] pb-[calc(env(safe-area-inset-bottom))] bg-repeat grid grid-rows-[auto,1fr]"
      style={{
        backgroundImage: `url('/assets/backgrounds/bg_blur_${colorMode === 'dark' ? 'dark' : 'light'}.png')`,
        backgroundRepeat: 'round'
      }}
    >
      <div className="px-[16px] py-[8px] flex items-center justify-start">
        <button type="button" onClick={handleHistoryBack}>
          <Xmark size={24} className="text-text-main-lv1" />
        </button>
      </div>
      <div className="grid place-items-center px-[24px] py-[24px] overflow-y-auto min-h-0">
        <div className={`${baseBoxColor} ${boxColor}`}>
          <div className="flex items-center justify-center gap-[7px] w-full">
            <ProfileAvatar
              thumbnailUrl={review.reviewer.thumbnailUrl || review.reviewer.pictureUrl}
              widthAndHeight="28px"
            />
            <div className="text-[15px] font-medium text-text-main-lv2 flex items-center">
              <Trans
                i18nKey="TradingPhotoCardReviewDetailPage.receivedReview"
                components={{
                  span: <span className="inline-block mr-[3px] font-bold max-w-[117px] truncate" />
                }}
                values={{ nickname: review.reviewer.nickname }}
              />
            </div>
          </div>

          {/* 포토카드 영역 */}
          {review.reviewableType === 'TradingPhotoCard'
            ? <TradingPhotoCardSection reviewable={review.reviewable} />
            : <MatchedTradingPhotoCardSection reviewable={review.reviewable} />}

          {review.pictureUrl && (
            <div className="mt-[19px]">
              <img
                src={review.pictureUrl}
                alt="review"
                className="rounded-[12px] w-[287px] h-[187px] object-cover"
              />
            </div>
          )}
          {/* 리뷰 내용 */}
          <p className="text-[15px] text-text-main-lv2 mt-[16px] whitespace-pre-line">
            {review.content}
          </p>
          {/* 피드백 옵션 */}
          <div className="mt-[16px] flex flex-col gap-[8px]">
            {review.feedbackOptions?.map((feedbackOption) => {
              return (
                <RoundBadge key={feedbackOption} size="medium" className="text-on-label">
                  {t(`ReviewFeedbackOptions.${capitalizeFirstLetter(feedbackOption)}`)}
                </RoundBadge>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingPhotoCardReviewDetailPage;

const TradingPhotoCardSection = ({ reviewable }) => {
  return (
    <div className="w-[287px] flex items-center gap-[8px] px-[15px] py-[13px] rounded-[12px] border border-surface-lv2 mt-[18px]">
      <div className="w-[35px] h-[55px]">
        <ThumbnailPhotoCard
          imageUrl={reviewable.photoCard.mediumThumbnailUrl || reviewable.photoCard.pictureUrl}
          className="rounded-[5px] w-[35px] h-[55px]"
        />
      </div>
      <div className="flex leading-none items-center justify-between w-full">
        <div className="flex flex-col gap-[6px]">
          <p className="text-[13px] font-semibold text-text-main-lv2 w-[157px]">
            {t('SelectTradingPhotoCardReviewPage.tradingPhotoCardName', {
              nickname: reviewable.photoCard.memberName,
              categoryName: reviewable.photoCard.categoryName,
              emoji: reviewable.photoCard.categoryEmoji
            })}
          </p>
          <p className="text-[12px] text-text-main-lv2 w-[157px] truncate">
            {reviewable.photoCard.eventName}
          </p>
        </div>
        <p className="text-[13px] text-text-primary font-semibold">{reviewable.price ?? '제시'}</p>
      </div>
    </div>
  );
};

const MatchedTradingPhotoCardSection = ({ reviewable }) => {
  return (
    <div className="w-[287px] flex items-center gap-[8px] px-[15px] py-[13px] rounded-[12px] border border-surface-lv2 mt-[18px]">
      <div className="relative w-[63px] h-[55px]">
        <ThumbnailPhotoCard
          imageUrl={
            reviewable.sourceCard.photoCard.smallThumbnailUrl ||
            reviewable.sourceCard.photoCard.pictureUrl
          }
          className="w-[28px] h-[44px] absolute left-0 top-[5px] z-0"
          variant="grayscale80"
        />
        <ThumbnailPhotoCard
          imageUrl={
            reviewable.targetCard.photoCard.mediumThumbnailUrl ||
            reviewable.targetCard.photoCard.pictureUrl
          }
          className="w-[35px] h-[55px] absolute right-0 top-0 z-10"
        />
      </div>
      <div className="flex items-center justify-between w-full">
        <div className="flex flex-col items-start gap-[6px]">
          <p className="text-[13px] font-semibold text-text-main-lv2 max-w-[130px] truncate">
            {t('SelectTradingPhotoCardReviewPage.tradingPhotoCardName', {
              nickname: reviewable.targetCard.photoCard.memberName,
              categoryName: reviewable.targetCard.photoCard.categoryName,
              emoji: reviewable.targetCard.photoCard.categoryEmoji
            })}
          </p>
          <p className="text-[13px] font-medium text-text-main-lv2 max-w-[130px] truncate leading-none">
            {reviewable.targetCard.photoCard.eventName}
          </p>
        </div>
        <div className="flex gap-[2px] items-center">
          <ArrowsDownUp className="text-text-primary" />
          <p className="text-[13px] font-semibold text-text-primary">교환</p>
        </div>
      </div>
    </div>
  );
};
